import { useContext, useState, useCallback, useEffect, useMemo } from "react";
import apiClient from "../../../../../helpers/api-client";
import { PortalControllerContext } from "../../../../context";
import useFetchProctoringData from "../../../settings/useFetchProctoringData";
import { usesModules } from "../verityAssessmentHelper";
import { Module } from "@verity/api-client/types";
import { CoreFileItem } from "@verity/components/core";

export type Course = {
	id: number;
	lmsCourseId: string;
	name?: string;
};

export default function useFetchVerityAssessmentInfo(quizId?: number) {
	const { portalContext } = useContext(PortalControllerContext);
	const { instituteId, lms, courseId } = portalContext;

	const [fetchingCourses, setFetchingCourses] = useState<boolean>(false);
	const [fetchingModules, setFetchingModules] = useState<boolean>(false);
	const [courses, setCourses] = useState<Course[]>([]);
	const [modules, setModules] = useState<Module[]>([]);
	const [instructorFiles, setInstructorFiles] = useState<CoreFileItem[]>([]);

	const { applications, extensions } = useFetchProctoringData();

	const fetchCourseModules = useCallback(
		async (courseId: number | undefined) => {
			if (!usesModules(lms) || !courseId) return;

			try {
				setFetchingModules(true);
				if (lms === "moodle") {
					const res = await apiClient.course.getCourseRootSections(courseId);
					setModules(res.sections);
				} else {
					const res = await apiClient.course.getCourseRootModules(courseId);
					setModules(res.modules);
				}
			} catch (e) {
				console.error(e);
			} finally {
				setFetchingModules(false);
			}
		},
		[lms],
	);

	useEffect(() => {
		void (async () => {
			if (instituteId === undefined) return;

			try {
				setFetchingCourses(true);

				const response = await apiClient.course.getInstituteCoursesInfo(instituteId);
				const courses = response.map(({ id, name, courseId }) => ({
					id,
					lmsCourseId: courseId,
					name: name ?? undefined,
				}));

				const filteredCourses = courses
					.filter((course) => Boolean(course.name))
					.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));

				setCourses(filteredCourses);
			} catch (e) {
				console.error(e);
			} finally {
				setFetchingCourses(false);
			}
		})();
	}, [instituteId]);

	useEffect(() => {
		void fetchCourseModules(courseId);
	}, [fetchCourseModules, courseId]);

	useEffect(() => {
		void (async () => {
			if (!quizId) return;
			try {
				const assessmentResources = await apiClient.proctoringConfiguration.getAssessmentResources(quizId);
				setInstructorFiles(assessmentResources);
			} catch (error) {
				console.error("Error fetching assessment resources:", error);
			}
		})();
	}, [quizId]);

	const loading = useMemo(
		() => fetchingCourses || (usesModules(lms) && fetchingModules),
		[fetchingCourses, fetchingModules, lms],
	);

	return { loading, courses, modules, applications, extensions, fetchCourseModules, fetchingModules, instructorFiles };
}

export type UseFetchVerityAssessmentInfoReturn = ReturnType<typeof useFetchVerityAssessmentInfo>;
